const FONT_WEIGHT_MAP: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const getFontWeightValue = (weight: string): number => FONT_WEIGHT_MAP[weight] ?? 400;

interface StyleParams {
  focusBorderColor: string;
  fontSize: number;
  fontWeight: string;
  fontFamilyCss: string;
  textColor: string;
  placeholderColor: string;
  submitBgColor: string;
  submitBgColorHover: string;
  mobileFontSize: number;
  mobilePaddingY: number;
  mobilePaddingX: number;
  mobileMinHeight: number;
  mobileIconGap: number;
  mobileIconSize: number;
  mobileSubmitButtonSize: number;
  /**
   * When true, apply the mobile-responsive rules unconditionally (instead of
   * inside an `@media (max-width: 767px)` block). Used for the panel/embedded
   * concierge window which is visually narrow even on desktop viewports.
   */
  forceMobile?: boolean;
}

export function buildPromptInputStyles(p: StyleParams): string {
  const mobileRules = `
      .concierge-prompt-input {
        --icon-size: ${p.mobileIconSize}px;
        --submit-button-size: ${p.mobileSubmitButtonSize}px;
        --font-size-small: ${p.mobileFontSize}px;
        --font-size-base: ${p.mobileFontSize}px;
      }
      .concierge-prompt-input textarea,
      .concierge-prompt-input textarea[data-slot="input-group-control"] {
        font-size: ${p.mobileFontSize}px !important;
      }
      .concierge-input-container {
        padding: ${p.mobilePaddingY}px ${p.mobilePaddingX}px !important;
      }
      .concierge-input-container:not(:has(textarea[data-expanded])) {
        min-height: ${p.mobileMinHeight > 0 ? `${p.mobileMinHeight}px` : 'auto'} !important;
      }
      .concierge-input-container:has(textarea[data-expanded]) {
        min-height: ${p.mobileMinHeight > 0 ? `${p.mobileMinHeight}px` : 'auto'} !important;
        height: auto !important;
        /* Extra 4px on top/left when expanded for breathing room */
        padding: ${p.mobilePaddingY + 4}px ${p.mobilePaddingX}px ${p.mobilePaddingY}px ${p.mobilePaddingX + 4}px !important;
      }
      .concierge-prompt-input-icons {
        gap: ${p.mobileIconGap}px !important;
      }`;

  const mobileBlock = p.forceMobile ? mobileRules : `@media (max-width: 767px) {${mobileRules}\n    }`;

  // iOS zooms the page whenever a focused form control computes to LESS than
  // 16px, and has ignored `user-scalable=no` / `maximum-scale` since iOS 10 —
  // there is no viewport-meta escape hatch, only the font size. Every ChatInput
  // preset ships `mobile_fontSize: 14`, so tapping the composer zoomed the whole
  // page in and dragged the layout with it. Floor it at 16 for WebKit-on-iOS
  // only (`-webkit-touch-callout` is iOS-exclusive; desktop Safari lacks it, and
  // every iOS browser is WebKit so they all need this) — Android has no such
  // rule and keeps whatever size the brand configured.
  const iosZoomGuardRules = `
      .concierge-prompt-input textarea,
      .concierge-prompt-input textarea[data-slot="input-group-control"] {
        font-size: ${Math.max(16, p.mobileFontSize)}px !important;
      }`;
  const iosZoomGuard = `@supports (-webkit-touch-callout: none) {${
    p.forceMobile ? iosZoomGuardRules : `\n      @media (max-width: 767px) {${iosZoomGuardRules}\n      }`
  }\n    }`;

  return `
    .concierge-prompt-input {
      --icon-size: auto;
      --submit-button-size: auto;
      --font-size-small: ${p.fontSize}px;
      --font-size-base: ${p.fontSize}px;
    }
    .concierge-prompt-input [data-slot="input-group"] {
      border: none !important;
      box-shadow: none !important;
      --tw-shadow: 0 0 #0000 !important;
      --tw-shadow-colored: 0 0 #0000 !important;
      background: transparent !important;
      padding: 0 !important;
      margin: 0 !important;
      min-height: 0 !important;
      height: auto !important;
      outline: none !important;
    }
    .concierge-prompt-input [data-slot="input-group"]:has([data-slot="input-group-control"]:focus-visible) {
      --tw-ring-color: transparent !important;
      --tw-ring-offset-width: 0 !important;
      --tw-ring-width: 0 !important;
      box-shadow: none !important;
    }
    .concierge-prompt-input [data-slot="input-group-control"]:focus-visible {
      outline: none !important;
      --tw-ring-color: transparent !important;
      box-shadow: none !important;
    }
    .concierge-prompt-input textarea,
    .concierge-prompt-input textarea:focus,
    .concierge-prompt-input textarea:focus-visible {
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      --tw-shadow: 0 0 #0000 !important;
      --tw-shadow-colored: 0 0 #0000 !important;
    }
    .concierge-prompt-input *:focus,
    .concierge-prompt-input *:focus-visible {
      outline: none !important;
      --tw-ring-color: transparent !important;
      --tw-ring-width: 0 !important;
      --tw-ring-offset-width: 0 !important;
    }
    .concierge-prompt-input:focus-within {
      outline: none !important;
    }
    /* The blanket suppression above exists so the composer reads as one box
       rather than a strip of separately-ringed controls. The BUTTONS in it are
       not part of that box, though - attach / mic / call / submit are discrete
       controls, and with the ring gone a keyboard user could not see where
       focus was (WCAG 2.4.7). Higher specificity than the universal rule
       above, so this wins for buttons only and the textarea stays ring-free. */
    .concierge-prompt-input button:focus-visible {
      outline: 2px solid ${p.textColor} !important;
      outline-offset: 2px !important;
    }
    .concierge-prompt-input textarea,
    .concierge-prompt-input textarea[data-slot="input-group-control"] {
      font-size: ${p.fontSize}px !important;
      font-weight: ${getFontWeightValue(p.fontWeight)} !important;
      font-family: ${p.fontFamilyCss} !important;
      color: ${p.textColor} !important;
      line-height: 1.5 !important;
    }
    .concierge-prompt-input textarea::placeholder {
      color: ${p.placeholderColor} !important;
    }

    ${mobileBlock}

    ${iosZoomGuard}
  `;
}
